import React, { createContext, ReactNode, useState } from "react";
import {PolymorphicItem} from "../tevapi/interfaces";
import {TEVService} from "../tevapi/io";
import {useQuery} from "@tanstack/react-query";

export type ItemRelationTypes = "siblings" | "delivery_siblings" | "sources";

interface IItemRelationContext {
  siblings: PolymorphicItem[];
  delivery_siblings: PolymorphicItem[];
  sources: PolymorphicItem[];
  assigned_atlas?: PolymorphicItem;
};

interface IItemComparingContext {
  relationContext: IItemRelationContext;
  relationsLoading: boolean;
  comparison: PolymorphicItem | null;
  setComparing: (referenceItemPK: number | null) => void;
  overrideComparing: (value: PolymorphicItem) => void;
};


export const ComparingItemContext = createContext<IItemComparingContext | null>(null);

const findItemByPk = <T extends { pk: number }>(
  data: Record<ItemRelationTypes, T[]>,
  wantedPk: number
): T | null => {
  // Iterate over each key in the data object
  for (const key of Object.keys(data)) {
    console.log(key, data[key]);
    // Search for the item in the array at this key
    const found = data[key].find((element) => element.pk === wantedPk);
    if (found) {
      return found;
    }
  }
  // If no matching item was found in any array
  return null;
};

export const ComparingStoreContext: React.FC<{pk: number, children: ReactNode[]}> = (props) => {
  const [siblingContext, _setSiblingContext] = useState<IItemRelationContext>({siblings: [], delivery_siblings: [], sources: []});
  const [comparison, setComparison] = useState<PolymorphicItem | null>(null);

  const setComparing = (referenceItemPK: number | null) => {
    if (referenceItemPK === null) { return null; };
    if (referenceItemPK === siblingContext.assigned_atlas?.pk) {
      setComparison(siblingContext.assigned_atlas);
    } else {
      const obj = findItemByPk<PolymorphicItem>({
        sources: siblingContext.sources,
        delivery_siblings: siblingContext.delivery_siblings,
        siblings: siblingContext.siblings,
      }, referenceItemPK);

      setComparison(obj);
    }
  };

  const relationQuery = useQuery<IItemRelationContext>({
    queryKey: ["items", props.pk, "relations"],
    queryFn: async () => {
      const response = await TEVService.get<IItemRelationContext>(
        `/itemdata/${props.pk}/relations/`
      );
      _setSiblingContext(response.data);
      if (response.data.assigned_atlas) {
        setComparison(response.data.assigned_atlas);
      }
      return response.data;
    },
  });

  const contextObj: IItemComparingContext = {
    relationContext: siblingContext,
    relationsLoading: relationQuery.isLoading,
    overrideComparing: setComparison,
    comparison,
    setComparing,
  };

  return (
    <ComparingItemContext.Provider value={contextObj}>
      {props.children}
    </ComparingItemContext.Provider>
  )
};
